import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import api from '../lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  if (!d) return '';
  const p = d.split('-');
  return p.length === 3 && p[0].length === 4 ? `${p[2]}-${p[1]}-${p[0].slice(-2)}` : d;
};

const fmtAmt = (v) => {
  if (!v && v !== 0) return '';
  return Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const ENTITY_TYPES = [
  { key: 'vendor', label: 'Vendor' },
  { key: 'customer', label: 'Customer' },
  { key: 'distributor', label: 'Distributor' },
  { key: 'agent', label: 'Agent' },
];

const getCurrentFY = () => {
  const now = new Date();
  const fyStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    start: `${fyStart}-04-01`,
    end: `${fyStart + 1}-03-31`,
    label: `FY ${String(fyStart).slice(2)}-${String(fyStart + 1).slice(2)}`,
  };
};

const FY_OPTIONS = (() => {
  const now = new Date();
  const currentFYStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const options = [];
  for (let y = currentFYStart; y >= currentFYStart - 3; y--) {
    options.push({
      start: `${y}-04-01`,
      end: `${y + 1}-03-31`,
      label: `FY ${String(y).slice(2)}-${String(y + 1).slice(2)}`,
    });
  }
  return options;
})();

export default function LedgerReport({ user, prefillEntity = null }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entityType, setEntityType] = useState(prefillEntity?.type || searchParams.get('type') || 'vendor');
  const [entityId, setEntityId] = useState(prefillEntity?.id || searchParams.get('id') || '');
  const [entitySearch, setEntitySearch] = useState('');
  const [entities, setEntities] = useState([]);
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');

  const fy = getCurrentFY();
  const [startDate, setStartDate] = useState(searchParams.get('start') || fy.start);
  const [endDate, setEndDate] = useState(searchParams.get('end') || fy.end);

  // Sync state to URL so back-navigation restores the selected filters
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    let changed = false;
    if (entityType && params.get('type') !== entityType) { params.set('type', entityType); changed = true; }
    if (entityId && params.get('id') !== entityId) { params.set('id', entityId); changed = true; }
    if (startDate && params.get('start') !== startDate) { params.set('start', startDate); changed = true; }
    if (endDate && params.get('end') !== endDate) { params.set('end', endDate); changed = true; }
    if (changed) setSearchParams(params, { replace: true });
  }, [entityType, entityId, startDate, endDate, searchParams, setSearchParams]);

  useEffect(() => {
    const load = async () => {
      try {
        const endpoint = entityType === 'vendor' ? '/vendors' : entityType === 'customer' ? '/customers' : entityType === 'distributor' ? '/distributors' : '/agents';
        const res = await api.get(endpoint);
        let list = res.data;
        if (entityType === 'customer') list = list.filter(c => c.is_active);
        list.sort((a, b) => ((a.company_name || a.name || '').localeCompare(b.company_name || b.name || '')));
        setEntities(list);
      } catch { toast.error('Failed to load entities'); }
    };
    load();
  }, [entityType]);

  useEffect(() => {
    if (!entityId) { setLedgerData(null); return; }
    const load = async () => {
      setLoading(true);
      try {
        let url = `/ledger/${entityType}/${entityId}`;
        const params = [];
        if (startDate) params.push(`start_date=${startDate}`);
        if (endDate) params.push(`end_date=${endDate}`);
        if (params.length) url += '?' + params.join('&');
        const res = await api.get(url);
        setLedgerData(res.data);
      } catch { toast.error('Failed to load ledger'); }
      finally { setLoading(false); }
    };
    load();
  }, [entityType, entityId, startDate, endDate]);

  useEffect(() => {
    if (entityId && entities.length) {
      const ent = entities.find(e => e.id === entityId);
      if (ent) setEntitySearch(ent.company_name || ent.name || '');
    }
  }, [entityId, entities]);

  const filteredEntities = useMemo(() => {
    if (!entitySearch.trim()) return entities;
    const q = entitySearch.trim().toLowerCase();
    return entities.filter(e =>
      (e.company_name || e.name || '').toLowerCase().includes(q) ||
      (e.city || '').toLowerCase().includes(q)
    );
  }, [entitySearch, entities]);

  const filteredTransactions = useMemo(() => {
    if (!ledgerData?.transactions) return [];
    if (typeFilter === 'all') return ledgerData.transactions;
    return ledgerData.transactions.filter(t => t.type === typeFilter);
  }, [ledgerData, typeFilter]);

  const filteredTotals = useMemo(() => {
    return {
      debit: filteredTransactions.reduce((s, t) => s + (t.debit || 0), 0),
      credit: filteredTransactions.reduce((s, t) => s + (t.credit || 0), 0),
    };
  }, [filteredTransactions]);

  const availableTypes = useMemo(() => {
    if (!ledgerData?.transactions) return [];
    return [...new Set(ledgerData.transactions.map(t => t.type))];
  }, [ledgerData]);

  const selectEntity = (ent) => {
    setEntityId(ent.id);
    setEntitySearch(ent.company_name || ent.name || '');
  };

  const clearEntity = () => {
    setEntityId('');
    setEntitySearch('');
    setLedgerData(null);
  };

  const applyFY = (fy) => {
    setStartDate(fy.start);
    setEndDate(fy.end);
  };

  const handleExport = async (format) => {
    if (!entityId) return;
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const url = `${API_BASE}/api/ledger/${entityType}/${entityId}/${format}?${params.toString()}`;
      const token = localStorage.getItem('token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      a.download = `Ledger_${ledgerData?.entity?.name || 'Report'}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch { toast.error(`Failed to export ${format}`); }
  };

  const isSales = ['customer', 'distributor', 'agent'].includes(entityType);

  return (
    <div className="p-6 space-y-4 animate-fadeIn" data-testid="ledger-report-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="ledger-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }} data-testid="ledger-title">Ledger Account</h1>
            <p className="text-sm text-gray-500 mt-0.5">Debit / Credit with Net {isSales ? 'Sales' : 'Purchase'} summary</p>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div>
              <Label className="text-xs font-semibold text-gray-600">Entity Type</Label>
              <select
                value={entityType}
                onChange={e => { setEntityType(e.target.value); setEntityId(''); setEntitySearch(''); setLedgerData(null); }}
                className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                data-testid="ledger-entity-type"
              >
                {ENTITY_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>

            <div className="md:col-span-2">
              <Label className="text-xs font-semibold text-gray-600">Search {ENTITY_TYPES.find(t => t.key === entityType)?.label}</Label>
              <Input
                value={entitySearch}
                onChange={e => setEntitySearch(e.target.value)}
                placeholder={`Filter ${entityType} list...`}
                className="h-9 text-sm"
                data-testid="ledger-entity-search"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold text-gray-600">From</Label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-9 text-sm" data-testid="ledger-start-date" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-600">To</Label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-9 text-sm" data-testid="ledger-end-date" />
            </div>
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className="text-xs text-gray-500">Quick:</span>
            {FY_OPTIONS.map(f => (
              <button key={f.label} onClick={() => applyFY(f)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${startDate === f.start && endDate === f.end ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                data-testid={`fy-btn-${f.label}`}>
                {f.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-800" />
        </div>
      )}

      {/* Tally-Style Ledger */}
      {ledgerData && !loading && (
        <>
          {/* Entity Header */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={clearEntity} data-testid="ledger-change-entity-btn">
                <ArrowLeft className="w-3.5 h-3.5" /> Change {ledgerData.entity.type}
              </Button>
              <h2 className="text-lg font-bold text-gray-900">{ledgerData.entity.name}</h2>
              {ledgerData.entity.city && <span className="text-sm text-gray-500">({ledgerData.entity.city})</span>}
              <Badge className="bg-gray-100 text-gray-700 border border-gray-300 text-xs capitalize">{ledgerData.entity.type}</Badge>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setTypeFilter('all')}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${typeFilter === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                data-testid="ledger-filter-all">
                All ({ledgerData.transactions.length})
              </button>
              {availableTypes.map(t => (
                <button key={t} onClick={() => setTypeFilter(t)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${typeFilter === t ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                  data-testid={`ledger-filter-${t}`}>
                  {t} ({ledgerData.transactions.filter(tr => tr.type === t).length})
                </button>
              ))}
              <span className="w-px h-5 bg-gray-300 mx-1" />
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-green-700 border-green-300 hover:bg-green-50" onClick={() => handleExport('excel')} data-testid="ledger-export-excel">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Excel
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-red-700 border-red-300 hover:bg-red-50" onClick={() => handleExport('pdf')} data-testid="ledger-export-pdf">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                PDF
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-600 font-semibold">Total {isSales ? 'Sales' : 'Purchase'} (Dr)</p>
              <p className="text-lg font-bold text-blue-800" data-testid="total-debit">{fmtAmt(ledgerData.total_debit)}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs text-red-600 font-semibold">Total Returns (Cr)</p>
              <p className="text-lg font-bold text-red-800" data-testid="total-credit">{fmtAmt(ledgerData.total_credit)}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-xs text-emerald-600 font-semibold">Net {isSales ? 'Sales' : 'Purchase'}</p>
              <p className="text-lg font-bold text-emerald-800" data-testid="net-amount">{fmtAmt(ledgerData.net_amount)}</p>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
              <p className="text-xs text-indigo-600 font-semibold">{isSales ? 'SO' : 'PO'} Fulfilment</p>
              <p className="text-lg font-bold text-indigo-800" data-testid="fulfilment-pct">{ledgerData.fulfilment_pct}%</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-600 font-semibold">Return Rate</p>
              <p className="text-lg font-bold text-amber-800" data-testid="return-rate">{ledgerData.return_rate_pct}%</p>
              <p className="text-[10px] text-gray-400">Avg {isSales ? 'Invoice' : 'GRN'}: {fmtAmt(ledgerData.avg_order_value)}</p>
            </div>
          </div>

          {/* Ledger Table */}
          <Card className="overflow-hidden border-2 border-gray-300">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="ledger-table" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-3 py-2.5 text-left font-bold w-28 border-r border-gray-700">Date</th>
                      <th className="px-3 py-2.5 text-left font-bold border-r border-gray-700">Particulars</th>
                      <th className="px-3 py-2.5 text-center font-bold w-20 border-r border-gray-700">Vch Type</th>
                      <th className="px-3 py-2.5 text-left font-bold w-40 border-r border-gray-700">Vch No.</th>
                      <th className="px-3 py-2.5 text-right font-bold w-32 border-r border-gray-700">Debit</th>
                      <th className="px-3 py-2.5 text-right font-bold w-32">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                          <p className="font-medium">No transactions found</p>
                          <p className="text-xs mt-1">Try adjusting the date range or filters</p>
                        </td>
                      </tr>
                    ) : (
                      filteredTransactions.map((tx) => {
                        const isRef = !tx.side;
                        return (
                          <tr key={tx.id}
                            className={`border-b border-gray-200 hover:bg-blue-50/40 cursor-pointer transition-colors ${isRef ? 'bg-yellow-50/40 italic' : ''}`}
                            onClick={() => navigate(`/ledger/${tx.type}/${tx.id}?from=${entityType}&fromId=${entityId}`)}
                            data-testid={`ledger-row-${tx.id}`}>
                            <td className="px-3 py-2 font-medium text-gray-900 border-r border-gray-200">
                              {fmtDate(tx.date)}
                            </td>
                            <td className="px-3 py-2 text-gray-800 border-r border-gray-200">
                              {tx.particulars || tx.type}
                              {isRef && <span className="text-xs text-gray-400 ml-1">(Ref)</span>}
                            </td>
                            <td className="px-3 py-2 text-center border-r border-gray-200">
                              <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold ${tx.side === 'Dr' ? 'bg-blue-100 text-blue-800' : tx.side === 'Cr' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-500'}`}>
                                {tx.type}
                              </span>
                            </td>
                            <td className="px-3 py-2 font-mono text-blue-700 font-semibold border-r border-gray-200">
                              {tx.number}
                              {tx.ref_number && <span className="block text-[10px] text-gray-500 font-normal">{tx.type === 'SE' ? 'Inv' : tx.type === 'PR' ? 'DN' : tx.type === 'SR' ? 'CN' : ''}: {tx.ref_number}</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono border-r border-gray-200">
                              {tx.debit ? <span className="font-semibold text-blue-800">{fmtAmt(tx.debit)}</span> : ''}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {tx.credit ? <span className="font-semibold text-red-800">{fmtAmt(tx.credit)}</span> : ''}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {filteredTransactions.length > 0 && (
                    <tfoot>
                      <tr className="bg-gray-200 border-t-2 border-gray-400">
                        <td colSpan={4} className="px-3 py-2.5 font-bold text-gray-800 text-right border-r border-gray-300">
                          Total
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono font-bold text-blue-900 border-r border-gray-300">
                          {fmtAmt(filteredTotals.debit)}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono font-bold text-red-900">
                          {fmtAmt(filteredTotals.credit)}
                        </td>
                      </tr>
                      <tr className="bg-emerald-800 text-white">
                        <td colSpan={4} className="px-3 py-2.5 font-bold text-right border-r border-emerald-700">
                          Net {isSales ? 'Sales' : 'Purchase'}
                        </td>
                        <td colSpan={2} className="px-3 py-2.5 text-right font-mono font-bold text-lg">
                          {fmtAmt(ledgerData.net_amount)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!entityId && !loading && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="text-base font-bold text-gray-800">
                {ENTITY_TYPES.find(t => t.key === entityType)?.label} List
                <span className="ml-2 text-xs font-normal text-gray-400">({filteredEntities.length})</span>
              </h2>
              <span className="text-xs text-gray-400">Click a {entityType} to view its ledger</span>
            </div>
            {filteredEntities.length === 0 ? (
              <div className="py-12 text-center text-gray-400">
                <p className="font-medium">No {entityType}s found{entitySearch ? ` matching "${entitySearch}"` : ''}</p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="ledger-entity-list">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500">
                      <th className="px-3 py-2 w-12 font-semibold">#</th>
                      <th className="px-3 py-2 font-semibold">Name</th>
                      <th className="px-3 py-2 font-semibold">City</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEntities.map((e, idx) => (
                      <tr
                        key={e.id}
                        onClick={() => selectEntity(e)}
                        className="border-b border-gray-100 last:border-0 hover:bg-emerald-50 cursor-pointer transition-colors group"
                        data-testid={`ledger-entity-option-${e.id}`}
                      >
                        <td className="px-3 py-2.5 text-gray-400 text-xs">{idx + 1}</td>
                        <td className="px-3 py-2.5 font-medium text-gray-800 group-hover:text-emerald-700">{e.company_name || e.name}</td>
                        <td className="px-3 py-2.5 text-gray-500">{e.city || '-'}</td>
                        <td className="px-3 py-2.5 text-right">
                          <ArrowLeft className="w-4 h-4 text-gray-300 rotate-180 inline-block group-hover:text-emerald-500" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
